import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Stack,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  AddShoppingCart as AddShoppingCartIcon,
  History as HistoryIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import * as productApi from '../api/product';
import * as carMakeApi from '../api/carMake';
import * as carModelApi from '../api/carModel';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../utils/i18n-utils';
import { useTheme } from '@mui/material';
import { getProductPurchases, addProductPurchase, getInventoryValuation } from '../api/productPurchase';
import { format } from 'date-fns';

// Interface for CarMake data type
interface CarMake {
  id: number;
  name: string;
  description?: string;
  active: boolean;
}

// Interface for CarModel data type
interface CarModel {
  id: number;
  name: string;
  description?: string;
  carMakeId: number;
  carMake?: CarMake;
  yearStart?: number;
  yearEnd?: number;
  active: boolean;
}

// Interface for Product data type
interface Product {
  id: number;
  carMake: string;
  carMakeId?: number;
  carMake_id?: number; 
  carMakeObj?: CarMake;
  model: string;
  carModelId?: number;
  carModel_id?: number;
  carModelObj?: CarModel;
  year: string;
  glassModel: string;
  glassType?: string;
  coveredTo: string;
  size: string;
  sz: string;
  acc: string;
  remark: string;
  itemPrice: number;
  fittingPrice: number;
  quantity: number;
  minQuantity: number;
  supplier: string;
  supplierPrice: number;
  lastPurchasePrice?: number;
  lastPurchaseDate?: string;
}

// Interface for Purchase data type
interface ProductPurchase {
  id: number;
  productId: number;
  quantity: number;
  purchasePrice: number;
  purchaseDate: string;
  remainingQuantity: number;
  supplier: string;
  supplierInvoice: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

// Interface for inventory valuation
interface InventoryValuation {
  method: string;
  totalValue: number;
  products: {
    id: number;
    name: string;
    quantity: number;
    averageCost: number;
    totalValue: number;
    purchases: {
      id: number;
      date: string;
      quantity: number;
      remainingQuantity: number;
      price: number;
      value: number;
    }[];
  }[];
}

// Custom toolbar with export, column visibility, and density options
function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, justifyContent: 'flex-start' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const { user } = useAuth(); // Get user from auth context
  const [products, setProducts] = useState<Product[]>([]);
  const [carMakes, setCarMakes] = useState<CarMake[]>([]); // New state for car makes
  const [carModels, setCarModels] = useState<CarModel[]>([]); // New state for car models
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<Partial<Product>>({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [fileInput, setFileInput] = useState<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);

  // State for tabular data display
  const [pageSizeOptions, setPageSizeOptions] = useState<number[]>([5, 10, 25, 50]);
  
  // State for purchases
  const [purchases, setPurchases] = useState<ProductPurchase[]>([]);
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [selectedProductForPurchase, setSelectedProductForPurchase] = useState<Product | null>(null);
  const [purchaseFormData, setPurchaseFormData] = useState({
    quantity: 1,
    purchasePrice: 0,
    purchaseDate: format(new Date(), 'yyyy-MM-dd'),
    supplier: '',
    supplierInvoice: '',
    notes: ''
  });
  
  // State for inventory valuation
  const [inventoryValuation, setInventoryValuation] = useState<InventoryValuation | null>(null);
  const [valuationMethod, setValuationMethod] = useState<'fifo' | 'lifo' | 'average'>('average');
  const [openValuationDialog, setOpenValuationDialog] = useState(false);
  
  // State for showing purchase history
  const [openPurchaseHistory, setOpenPurchaseHistory] = useState(false);
  
  // Ref to prevent double submission (especially in React.StrictMode)
  const isSubmittingPurchase = useRef(false);

  // Check if user has manager or admin role
  const isManager = user?.role === 'manager' || user?.role === 'admin';

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
    fetchCarMakes(); // Also fetch car makes
    fetchCarModels(); // Also fetch car models
  }, []);

  const fetchProducts = async (retryCount = 0) => {
    setLoading(true);
    try {
      console.log('Attempting to fetch products...');
      const data = await productApi.getAllProducts();
      console.log(`Products fetched successfully. Count: ${data.length}`);
      
      if (data.length === 0 && retryCount < 2) {
        // If we got an empty array, try again after a short delay
        console.log(`No products returned. Retrying (${retryCount + 1}/2)...`);
        setTimeout(() => fetchProducts(retryCount + 1), 1000);
        return;
      }
      
      // Check if car make and model relationships are present
      if (data.length > 0) {
        console.log('First product sample:', data[0]);
        console.log('First product carMakeObj:', data[0].carMakeObj);
        console.log('First product carModelObj:', data[0].carModelObj);
      } else {
        console.log('No products found in the database');
      }
      
      // Preprocess the data to flatten the nested objects for display
      const processedProducts = data.map((product: any) => ({
        ...product,
        // Add explicit properties for DataGrid to display
        displayMake: product.carMakeObj ? product.carMakeObj.name : (product.carMake || ''),
        displayModel: product.carModelObj ? product.carModelObj.name : (product.model || '')
      }));
      
      setProducts(processedProducts);
    } catch (error: any) {
      console.error('Error fetching products:', error);
      
      // Check if it's an authentication error
      if (error.response && error.response.status === 401) {
        showSnackbar(t('inventory.notifications.error', { message: t('inventory.notifications.sessionExpired') }), 'error');
        // You might want to redirect to login page or refresh token here
      } else {
        showSnackbar(t('inventory.notifications.error', { message: error.message || t('inventory.notifications.unknownError') }), 'error');
        
        // Retry on network errors
        if ((error.message && error.message.includes('network')) && retryCount < 2) {
          console.log(`Network error. Retrying (${retryCount + 1}/2)...`);
          setTimeout(() => fetchProducts(retryCount + 1), 1000);
          return;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch car makes
  const fetchCarMakes = async () => {
    try {
      const data = await carMakeApi.getAllCarMakes();
      console.log('Car makes fetched:', data); // Debug log
      setCarMakes(data);
    } catch (error) {
      showSnackbar('Failed to load car makes', 'error');
      console.error('Error fetching car makes:', error);
    }
  };

  // Fetch car models
  const fetchCarModels = async () => {
    try {
      const data = await carModelApi.getAllCarModels();
      console.log('Car models fetched:', data); // Debug log
      setCarModels(data);
    } catch (error) {
      showSnackbar('Failed to load car models', 'error');
      console.error('Error fetching car models:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // Define column configurations with actions
  const columns: GridColDef[] = [
    { 
      field: 'displayMake', 
      headerName: t('inventory.columns.carMake'), 
      width: 80, 
      flex: 1
    },
    { 
      field: 'displayModel', 
      headerName: t('inventory.columns.model'), 
      width: 80, 
      flex: 1
    },
    { field: 'year', headerName: t('inventory.columns.year'), width: 60, flex: 0.7 },
    { field: 'glassModel', headerName: t('inventory.columns.glass'), width: 80, flex: 1 },
    { field: 'glassType', headerName: t('inventory.columns.glassType'), width: 80, flex: 1 },
    { field: 'coveredTo', headerName: t('inventory.columns.covered'), width: 80, flex: 1 },
    { field: 'size', headerName: t('inventory.columns.size'), width: 60, flex: 0.7 },
    { field: 'sz', headerName: t('inventory.columns.sz'), width: 50, flex: 0.5 },
    { field: 'acc', headerName: t('inventory.columns.acc'), width: 50, flex: 0.5 },
    { field: 'remark', headerName: t('inventory.columns.remark'), width: 100, flex: 1.2 },
    { field: 'itemPrice', headerName: t('inventory.columns.item'), width: 60, type: 'number', flex: 0.7 },
    { field: 'fittingPrice', headerName: t('inventory.columns.fitting'), width: 60, type: 'number', flex: 0.7 },
    { field: 'quantity', headerName: t('inventory.columns.qty'), width: 50, type: 'number', flex: 0.5 },
    { field: 'minQuantity', headerName: t('inventory.columns.min'), width: 50, type: 'number', flex: 0.5 },
    { field: 'supplier', headerName: t('inventory.columns.supplier'), width: 80, flex: 1 },
    { field: 'supplierPrice', headerName: t('inventory.columns.suppPrice'), width: 80, type: 'number', flex: 1 },
    {
      field: 'actions',
      headerName: t('inventory.columns.actions'),
      width: 120,
      renderCell: (params) => (
        <Box>
                <IconButton
            color="primary"
                  size="small"
                  onClick={() => handleEdit(params.row as Product)}
            aria-label={t('inventory.actions.edit')}
                >
            <EditIcon />
                </IconButton>
                <IconButton
            color="error"
                  size="small"
                  onClick={() => handleDelete(params.id)}
            aria-label={t('inventory.actions.delete')}
                >
            <DeleteIcon />
                </IconButton>
              <IconButton
            color="secondary"
                size="small"
            onClick={() => handleOpenPurchaseDialog(params.row as Product)}
            aria-label={t('inventory.actions.purchase')}
              >
            <AddShoppingCartIcon />
              </IconButton>
          <IconButton
            color="info"
            size="small"
            onClick={() => handleViewPurchaseHistory(params.row as Product)}
            aria-label={t('inventory.actions.history')}
          >
            <HistoryIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleAdd = () => {
    setSelectedProduct(null);
    setFormData({});
    setOpenDialog(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    
    // Get the carMakeId and carModelId from either direct or _id property (depending on API response)
    const carMakeId = product.carMakeId || product.carMake_id;
    const carModelId = product.carModelId || product.carModel_id;
    
    setFormData({
      ...product, 
      carMakeId,
      carModelId
    });
    
    // Fetch car models for this make
    if (carMakeId) {
      fetchCarModelsForMake(carMakeId);
    }
    
    setOpenDialog(true);
  };

  const handleDelete = async (id: GridRowId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setLoading(true);
      try {
        await productApi.deleteProduct(id);
        const updatedProducts = products.filter(product => product.id !== id);
        setProducts(updatedProducts);
        showSnackbar(t('inventory.notifications.deleteSuccess'));
      } catch (error) {
        console.error('Error deleting product:', error);
        showSnackbar(t('inventory.notifications.error', { message: t('inventory.notifications.deleteFailed') }), 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.carMakeId || !formData.carModelId) {
      alert(t('inventory.notifications.selectBothMakeAndModel'));
      return;
    }

    setLoading(true);
    try {
      // Find the selected car model to get its name
      const selectedCarModel = carModels.find(model => model.id === formData.carModelId);
      if (!selectedCarModel) {
        throw new Error(t('inventory.notifications.modelNotFound'));
      }

      const productData = {
        ...formData,
        // Add the model name from the selected car model
        model: selectedCarModel.name,
        // Ensure numeric fields are numbers
        itemPrice: Number(formData.itemPrice || 0),
        fittingPrice: Number(formData.fittingPrice || 0),
        quantity: Number(formData.quantity || 0),
        minQuantity: Number(formData.minQuantity || 0),
        supplierPrice: Number(formData.supplierPrice || 0)
      };

      if (selectedProduct) {
        // Update existing product
        const response = await productApi.updateProduct(selectedProduct.id, productData);
        
        // Update product in the local state
        const updatedProducts = products.map(product => 
          product.id === selectedProduct.id ? response.product : product
        );
        setProducts(updatedProducts);
        showSnackbar(t('inventory.notifications.editSuccess'));
      } else {
        // Create new product
        const response = await productApi.createProduct(productData);
        
        // Add new product to the local state
        setProducts([...products, response.product]);
        showSnackbar(t('inventory.notifications.addSuccess'));
      }
      
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving product:', error);
      showSnackbar(t('inventory.notifications.error', { message: t('inventory.notifications.saveFailed') }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = () => {
    // Create a hidden file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    setFileInput(input);
    
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const csv = event.target?.result as string;
          const lines = csv.split('\n');
          const headers = lines[0].split(',');
          
          // Process CSV data and create products
          setLoading(true);
          let successCount = 0;
          let errorCount = 0;
          
          try {
            // Skip header row
            for (let i = 1; i < lines.length; i++) {
              if (lines[i].trim() === '') continue;
              
              const values = lines[i].split(',');
              const productData: any = {};
              
              // Map CSV columns to product properties
              headers.forEach((header, index) => {
                const key = header.trim();
                const value = values[index]?.trim();
                
                if (key === 'id') return; // Skip id as it's generated by the server
                
                if (['itemPrice', 'quantity', 'minQuantity', 'supplierPrice'].includes(key)) {
                  productData[key] = parseFloat(value) || 0;
                } else {
                  productData[key] = value || '';
                }
              });
              
              try {
                // Create the product
                await productApi.createProduct(productData);
                successCount++;
              } catch (error) {
                console.error('Error importing product:', error);
                errorCount++;
              }
            }
            
            // Refresh the product list after import
            await fetchProducts();
            
            // Show result message
            if (errorCount === 0) {
              showSnackbar(t('inventory.notifications.importSuccess', { count: successCount }));
            } else {
              showSnackbar(t('inventory.notifications.importError', { success: successCount, errors: errorCount }), 'error');
            }
          } catch (error) {
            console.error('Error importing products:', error);
            showSnackbar(t('inventory.notifications.error', { message: t('inventory.notifications.importFailed') }), 'error');
          } finally {
            setLoading(false);
          }
        };
        
        reader.readAsText(file);
      }
    };
    
    input.click();
  };

  const handleExport = () => {
    // Creating CSV content
    const headers = [
      'id', 'carMake', 'model', 'year', 
      'glassModel', 'glassType', 'coveredTo', 'size', 'sz', 'acc', 'remark',
      'itemPrice', 'fittingPrice', 'quantity', 'minQuantity', 
      'supplier', 'supplierPrice'
    ];
    const csvContent = [
      headers.join(','),
      ...products.map(product => 
        headers.map(header => product[header as keyof Product]).join(',')
      )
    ].join('\n');
    
    // Creating and triggering download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'inventory_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSnackbar(t('inventory.notifications.exportSuccess'));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Handle select changes for car make dropdown
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    
    // If car make changes, load the models for that make and reset the selected model
    if (name === 'carMakeId') {
      fetchCarModelsForMake(value);
      setFormData({ 
        ...formData, 
        [name]: value,
        carModelId: undefined // Reset car model selection when make changes
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  // Fetch car models for a specific make
  const fetchCarModelsForMake = async (carMakeId: number) => {
    if (!carMakeId) return;
    
    try {
      const data = await carModelApi.getAllCarModels({ carMakeId });
      console.log(`Car models fetched for make ${carMakeId}:`, data);
      setCarModels(data);
    } catch (error) {
      showSnackbar('Failed to load car models for the selected make', 'error');
      console.error('Error fetching car models for make:', error);
    }
  };

  // Function to load product purchase history
  const loadProductPurchases = async (productId: number) => {
    try {
      setLoading(true);
      const data = await getProductPurchases(productId);
      setPurchases(data);
    } catch (error) {
      console.error('Error loading product purchases:', error);
      showSnackbar(t('inventory.error.purchaseHistory'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to add a new product purchase
  const handleAddPurchase = async () => {
    if (!selectedProductForPurchase) return;
    
    // Prevent double submission (handles React.StrictMode double-invocation)
    if (loading || isSubmittingPurchase.current) {
      console.log('Purchase submission already in progress, ignoring duplicate call');
      return;
    }
    
    try {
      isSubmittingPurchase.current = true;
      setLoading(true);
      
      const purchaseData = {
        productId: selectedProductForPurchase.id,
        ...purchaseFormData
      };
      
      const result = await addProductPurchase(purchaseData);
      
      // Refresh the product list to show updated quantity
      fetchProducts();
      
      // If purchase history dialog is open, refresh purchases
      if (openPurchaseHistory) {
        loadProductPurchases(selectedProductForPurchase.id);
      }
      
      // Reset form and close dialog
      setPurchaseFormData({
        quantity: 1,
        purchasePrice: 0,
        purchaseDate: format(new Date(), 'yyyy-MM-dd'),
        supplier: '',
        supplierInvoice: '',
        notes: ''
      });
      setOpenPurchaseDialog(false);
      
      showSnackbar(t('inventory.success.purchaseAdded'), 'success');
    } catch (error) {
      console.error('Error adding product purchase:', error);
      showSnackbar(t('inventory.error.addPurchase'), 'error');
    } finally {
      setLoading(false);
      // Reset the ref after a short delay to allow for any pending calls to complete
      setTimeout(() => {
        isSubmittingPurchase.current = false;
      }, 1000);
    }
  };

  // Function to view purchase history for a product
  const handleViewPurchaseHistory = (product: Product) => {
    setSelectedProductForPurchase(product);
    loadProductPurchases(product.id);
    setOpenPurchaseHistory(true);
  };

  // Function to open add purchase dialog
  const handleOpenPurchaseDialog = (product: Product) => {
    setSelectedProductForPurchase(product);
    // Set default purchase price to last purchase price or supplier price
    setPurchaseFormData({
      ...purchaseFormData,
      purchasePrice: product.lastPurchasePrice || product.supplierPrice || 0,
      supplier: product.supplier || ''
    });
    setOpenPurchaseDialog(true);
  };

  // Function to load inventory valuation report
  const loadInventoryValuation = async () => {
    try {
      setLoading(true);
      const data = await getInventoryValuation(valuationMethod);
      setInventoryValuation(data as InventoryValuation);
      setOpenValuationDialog(true);
    } catch (error) {
      console.error('Error loading inventory valuation:', error);
      showSnackbar(t('inventory.error.valuation'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                mb: 0.5,
                color: '#101D68',
                textAlign: isRTL ? 'right' : 'left' 
              }}
            >
              {t('inventory.title')}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6B7280',
                textAlign: isRTL ? 'right' : 'left' 
              }}
            >
              Manage your product inventory
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {isManager && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={handleImport}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t('inventory.import')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0px 4px 12px rgba(16, 29, 104, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0A1445 0%, #101D68 100%)',
                      boxShadow: '0px 6px 16px rgba(16, 29, 104, 0.4)',
                    },
                  }}
                >
                  {t('inventory.addProduct')}
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || products.length === 0}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#101D68',
                color: '#101D68',
                '&:hover': {
                  borderColor: '#0A1445',
                  backgroundColor: 'rgba(16, 29, 104, 0.05)',
                },
              }}
            >
              {t('inventory.export')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AttachMoneyIcon />}
              onClick={loadInventoryValuation}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#101D68',
                color: '#101D68',
                '&:hover': {
                  borderColor: '#0A1445',
                  backgroundColor: 'rgba(16, 29, 104, 0.05)',
                },
              }}
            >
              {t('inventory.valuation.title')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Paper sx={{ 
        width: '100%', 
        overflow: 'hidden', 
        position: 'relative', 
        minHeight: '300px',
        borderRadius: 3,
        boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
      }}>
        {loading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1,
            backdropFilter: 'blur(4px)',
          }}>
            <CircularProgress size={60} sx={{ color: '#101D68' }} />
            <Typography sx={{ mt: 2 }}>
              {t('inventory.loading')}
            </Typography>
          </Box>
        )}
        {!loading && products.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            p: 4,
            height: '300px'
          }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('inventory.noProducts')}
            </Typography>
            <Typography color="text.secondary" align="center" paragraph>
              {t('inventory.emptyInventory')}
            </Typography>
            {isManager && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                color="primary"
              >
                {t('inventory.addProduct')}
              </Button>
            )}
          </Box>
        )}
        {products.length > 0 && (
          <DataGrid
            rows={products}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
              columns: {
                columnVisibilityModel: {
                  // All columns visible by default
                },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              toolbar: CustomToolbar,
            }}
            autoHeight 
            density="compact"
            disableRowSelectionOnClick
            columnHeaderHeight={36}
            rowHeight={36}
            sx={{
              '& .MuiDataGrid-cell': {
                py: '2px',
                px: '8px',
                fontSize: '0.75rem', // Smaller font size
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: (theme) => theme.palette.primary.main,
                color: 'white',
                paddingLeft: '8px',
                paddingRight: '4px',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
                fontSize: '0.75rem', // Smaller font size
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none', // Remove column separators
              },
              border: 0,
              borderRadius: 1,
              '& .MuiDataGrid-row': {
                '&:nth-of-type(even)': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              },
              width: '100%',
            }}
          />
        )}
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedProduct ? t('inventory.editProduct') : t('inventory.addProduct')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="carMake-label">{t('inventory.form.carMake')}</InputLabel>
                  <Select
                    labelId="carMake-label"
                    id="carMakeId"
                    name="carMakeId"
                    value={formData.carMakeId || ''}
                    onChange={handleSelectChange}
                    label={t('inventory.form.carMake')}
                  >
                    {carMakes.map((make) => (
                      <MenuItem key={make.id} value={make.id}>
                        {make.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t('inventory.form.carMakeHelp')}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="carModel-label">{t('inventory.form.carModel')}</InputLabel>
                  <Select
                    labelId="carModel-label"
                    id="carModelId"
                    name="carModelId"
                    value={formData.carModelId || ''}
                    onChange={handleSelectChange}
                    label={t('inventory.form.carModel')}
                  >
                    {carModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t('inventory.form.carModelHelp')}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.year')}
                  name="year"
                  value={formData.year || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.glassModel')}
                  name="glassModel"
                  value={formData.glassModel || ''}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="glass-type-label">{t('inventory.form.glassType')}</InputLabel>
                  <Select
                    labelId="glass-type-label"
                    id="glassType"
                    name="glassType"
                    value={formData.glassType || ''}
                    onChange={handleSelectChange}
                    label={t('inventory.form.glassType')}
                  >
                    <MenuItem value="front">{t('inventory.glassTypes.front')}</MenuItem>
                    <MenuItem value="back">{t('inventory.glassTypes.back')}</MenuItem>
                    <MenuItem value="passenger">{t('inventory.glassTypes.passenger')}</MenuItem>
                    <MenuItem value="driver">{t('inventory.glassTypes.driver')}</MenuItem>
                    <MenuItem value="quarter">{t('inventory.glassTypes.quarter')}</MenuItem>
                    <MenuItem value="sunroof">{t('inventory.glassTypes.sunroof')}</MenuItem>
                    <MenuItem value="other">{t('inventory.glassTypes.other')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.coveredTo')}
                  name="coveredTo"
                  value={formData.coveredTo || ''}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.size')}
                  name="size"
                  value={formData.size || ''}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.sz')}
                  name="sz"
                  value={formData.sz || ''}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.acc')}
                  name="acc"
                  value={formData.acc || ''}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.remark')}
                  name="remark"
                  value={formData.remark || ''}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.itemPrice')}
                  name="itemPrice"
                  type="number"
                  value={formData.itemPrice || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.fittingPrice')}
                  name="fittingPrice"
                  type="number"
                  value={formData.fittingPrice || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.quantity')}
                  name="quantity"
                  type="number"
                  value={formData.quantity || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.minQuantity')}
                  name="minQuantity"
                  type="number"
                  value={formData.minQuantity || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.supplier')}
                  name="supplier"
                  value={formData.supplier || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('inventory.form.supplierPrice')}
                  name="supplierPrice"
                  type="number"
                  value={formData.supplierPrice || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={loading}>
            {t('inventory.form.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t('inventory.form.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Purchase Dialog */}
      <Dialog
        open={openPurchaseDialog}
        onClose={() => setOpenPurchaseDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('inventory.purchaseDialog.title')}
          {selectedProductForPurchase && (
            <Typography variant="subtitle1">
              {selectedProductForPurchase.carMake} {selectedProductForPurchase.model} - {selectedProductForPurchase.glassModel}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('inventory.purchaseDialog.quantity')}
                name="quantity"
                type="number"
                fullWidth
                value={purchaseFormData.quantity}
                onChange={(e) => setPurchaseFormData({
                  ...purchaseFormData,
                  quantity: parseInt(e.target.value)
                })}
                InputProps={{ inputProps: { min: 1 } }}
                required
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('inventory.purchaseDialog.price')}
                name="purchasePrice"
                type="number"
                fullWidth
                value={purchaseFormData.purchasePrice}
                onChange={(e) => setPurchaseFormData({
                  ...purchaseFormData,
                  purchasePrice: parseFloat(e.target.value)
                })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                required
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('inventory.purchaseDialog.date')}
                name="purchaseDate"
                type="date"
                fullWidth
                value={purchaseFormData.purchaseDate}
                onChange={(e) => setPurchaseFormData({
                  ...purchaseFormData,
                  purchaseDate: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('inventory.purchaseDialog.supplier')}
                name="supplier"
                fullWidth
                value={purchaseFormData.supplier}
                onChange={(e) => setPurchaseFormData({
                  ...purchaseFormData,
                  supplier: e.target.value
                })}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('inventory.purchaseDialog.invoice')}
                name="supplierInvoice"
                fullWidth
                value={purchaseFormData.supplierInvoice}
                onChange={(e) => setPurchaseFormData({
                  ...purchaseFormData,
                  supplierInvoice: e.target.value
                })}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('inventory.purchaseDialog.notes')}
                name="notes"
                fullWidth
                multiline
                rows={3}
                value={purchaseFormData.notes}
                onChange={(e) => setPurchaseFormData({
                  ...purchaseFormData,
                  notes: e.target.value
                })}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="textSecondary">
                {t('inventory.purchaseDialog.total')}: د.ل{(purchaseFormData.quantity * purchaseFormData.purchasePrice).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPurchaseDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAddPurchase}
            disabled={loading || !purchaseFormData.quantity || !purchaseFormData.purchasePrice}
          >
            {loading ? <CircularProgress size={24} /> : t('inventory.purchaseDialog.add')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Purchase History Dialog */}
      <Dialog
        open={openPurchaseHistory}
        onClose={() => setOpenPurchaseHistory(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {t('inventory.purchaseHistory.title')}
          {selectedProductForPurchase && (
            <Typography variant="subtitle1">
              {selectedProductForPurchase.carMake} {selectedProductForPurchase.model} - {selectedProductForPurchase.glassModel}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('inventory.purchaseHistory.date')}</TableCell>
                    <TableCell align="right">{t('inventory.purchaseHistory.quantity')}</TableCell>
                    <TableCell align="right">{t('inventory.purchaseHistory.remaining')}</TableCell>
                    <TableCell align="right">{t('inventory.purchaseHistory.price')}</TableCell>
                    <TableCell align="right">{t('inventory.purchaseHistory.total')}</TableCell>
                    <TableCell>{t('inventory.purchaseHistory.supplier')}</TableCell>
                    <TableCell>{t('inventory.purchaseHistory.invoice')}</TableCell>
                    <TableCell>{t('inventory.purchaseHistory.notes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.length > 0 ? (
                    purchases.map((purchase) => (
                      <TableRow key={purchase.id}>
                        <TableCell>{new Date(purchase.purchaseDate).toLocaleDateString()}</TableCell>
                        <TableCell align="right">{purchase.quantity}</TableCell>
                        <TableCell align="right">{purchase.remainingQuantity}</TableCell>
                        <TableCell align="right">د.ل{purchase.purchasePrice.toFixed(2)}</TableCell>
                        <TableCell align="right">د.ل{(purchase.quantity * purchase.purchasePrice).toFixed(2)}</TableCell>
                        <TableCell>{purchase.supplier || '-'}</TableCell>
                        <TableCell>{purchase.supplierInvoice || '-'}</TableCell>
                        <TableCell>{purchase.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {t('inventory.purchaseHistory.noPurchases')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPurchaseHistory(false)}>
            {t('common.close')}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              setOpenPurchaseHistory(false);
              handleOpenPurchaseDialog(selectedProductForPurchase!);
            }}
            disabled={!selectedProductForPurchase}
          >
            {t('inventory.purchaseHistory.addNew')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Inventory Valuation Dialog */}
      <Dialog
        open={openValuationDialog}
        onClose={() => setOpenValuationDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {t('inventory.valuation.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('inventory.valuation.method')}</InputLabel>
              <Select
                value={valuationMethod}
                label={t('inventory.valuation.method')}
                onChange={(e) => setValuationMethod(e.target.value as 'fifo' | 'lifo' | 'average')}
              >
                <MenuItem value="fifo">{t('inventory.valuation.fifo')}</MenuItem>
                <MenuItem value="lifo">{t('inventory.valuation.lifo')}</MenuItem>
                <MenuItem value="average">{t('inventory.valuation.average')}</MenuItem>
              </Select>
            </FormControl>
            <Button 
              variant="contained" 
              sx={{ ml: 2 }}
              onClick={loadInventoryValuation}
            >
              {t('inventory.valuation.calculate')}
            </Button>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : inventoryValuation ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('inventory.valuation.totalValue')}: د.ل{inventoryValuation.totalValue.toFixed(2)}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inventory.valuation.product')}</TableCell>
                      <TableCell align="right">{t('inventory.valuation.quantity')}</TableCell>
                      <TableCell align="right">{t('inventory.valuation.averageCost')}</TableCell>
                      <TableCell align="right">{t('inventory.valuation.totalValue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryValuation.products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">{product.quantity}</TableCell>
                        <TableCell align="right">د.ل{product.averageCost.toFixed(2)}</TableCell>
                        <TableCell align="right">د.ل{product.totalValue.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography variant="body1">
              {t('inventory.valuation.selectMethod')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenValuationDialog(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory; 